// Locutions — fixed multi-word phrases that behave as a single part of speech
// (a menudo = "often", a pesar de = "in spite of"). Learners should memorise
// them whole. The 30 most common sit in a reference table at the bottom.
import { renderGuideSections } from "../ui/guideSections.js";

const COMMON = [
  ["a menudo", "often", "Viene a menudo."],
  ["a veces", "sometimes", "A veces llueve."],
  ["de vez en cuando", "from time to time", "Salgo de vez en cuando."],
  ["por supuesto", "of course", "Por supuesto que sí."],
  ["sin embargo", "however", "Es caro; sin embargo, lo compro."],
  ["por lo tanto", "therefore", "Llueve, por lo tanto me quedo."],
  ["a lo mejor", "maybe", "A lo mejor viene."],
  ["de repente", "suddenly", "De repente se fue."],
  ["en seguida", "right away", "Vuelvo en seguida."],
  ["a pesar de", "in spite of", "A pesar de todo, sonríe."],
  ["por fin", "finally", "¡Por fin llegaste!"],
  ["en cuanto a", "regarding", "En cuanto a eso, no sé."],
  ["a partir de", "starting from", "A partir de hoy."],
  ["a través de", "through / across", "A través de la ventana."],
  ["al final", "in the end", "Al final lo logró."],
  ["por ejemplo", "for example", "Frutas, por ejemplo, manzanas."],
  ["es decir", "that is to say", "Mañana, es decir, el lunes."],
  ["o sea", "in other words", "O sea, no quieres."],
  ["de hecho", "in fact", "De hecho, ya lo sabía."],
  ["en realidad", "actually", "En realidad no importa."],
  ["por cierto", "by the way", "Por cierto, ¿y Ana?"],
  ["de nuevo", "again", "Inténtalo de nuevo."],
  ["ya no", "no longer", "Ya no vive aquí."],
  ["todavía no", "not yet", "Todavía no ha llegado."],
  ["al menos", "at least", "Al menos lo intentaste."],
  ["sobre todo", "above all", "Me gusta, sobre todo el final."],
  ["en vez de", "instead of", "Té en vez de café."],
  ["a lo largo de", "throughout", "A lo largo del día."],
  ["por lo menos", "at least", "Por lo menos tres."],
  ["en fin", "anyway / in short", "En fin, vámonos."],
];

export function locutionsView(app) {
  const table = `
    <div class="bg-white rounded-2xl border border-sage-100 p-5 mt-6">
      <h3 class="text-lg font-bold text-sage-800 mb-3">30 Most Common Locutions</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Spanish</th><th class="py-2 px-3">English</th><th class="py-2 px-3">Example</th>
        </tr></thead>
        <tbody>
          ${COMMON
            .map(
              ([es, en, ex]) => `<tr class="border-b border-sage-50 last:border-0">
            <td class="py-2 pr-3 font-medium text-sage-800">${es}</td>
            <td class="py-2 px-3 text-sage-700">${en}</td>
            <td class="py-2 px-3 text-sage-500 italic">${ex}</td>
          </tr>`
            )
            .join("")}
        </tbody>
      </table></div>
    </div>`;

  renderGuideSections(
    app,
    "Locutions",
    "A locution (locución) is a fixed group of words that works as a single unit — one adverb, one preposition, or one conjunction made of several words. Because the meaning lives in the whole phrase, you learn them as set pieces, not word by word.",
    [
      {
        title: "1. What counts as a locution",
        body: `
          <p class="text-sage-700 mb-2">If you can't swap or remove a word without breaking the meaning, you're looking at a locution. <i>A pesar de</i> means "in spite of" as a block — none of <i>a</i>, <i>pesar</i>, or <i>de</i> carries that meaning alone.</p>
          <p class="text-sage-700 text-sm">They're the connective tissue of fluent Spanish: dropping a few into your speech (<i>sin embargo</i>, <i>de hecho</i>, <i>por lo tanto</i>) instantly makes it sound more natural.</p>
        `,
      },
      {
        title: "2. The main types",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>Adverbial</b> (act like an adverb): <i>a menudo</i> (often), <i>de repente</i> (suddenly), <i>en seguida</i> (right away).</li>
            <li><b>Prepositional</b> (act like a preposition): <i>a pesar de</i> (in spite of), <i>a través de</i> (through), <i>en vez de</i> (instead of).</li>
            <li><b>Conjunctive</b> (link clauses): <i>sin embargo</i> (however), <i>por lo tanto</i> (therefore), <i>es decir</i> (that is).</li>
            <li><b>Verbal</b> (a verb + fixed words): <i>darse cuenta de</i> (to realise), <i>tener en cuenta</i> (to bear in mind), <i>echar de menos</i> (to miss).</li>
          </ul>
        `,
      },
      {
        title: "3. Using them as connectors",
        body: `
          <p class="text-sage-700 mb-2">Connective locutions usually open or pivot a sentence and are set off with commas:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>Estaba cansado. <b>Sin embargo</b>, terminó el trabajo.</i></li>
            <li><i>No tengo tiempo; <b>por lo tanto</b>, no puedo ir.</i></li>
            <li><i><b>De hecho</b>, ya lo había leído.</i></li>
          </ul>
          <p class="text-sage-700 text-sm mt-3">Mind the close pairs: <i>o sea</i> ≈ <i>es decir</i> (rephrasing), while <i>a lo mejor</i> ≈ <i>quizás</i> (maybe) — but <i>a lo mejor</i> takes the indicative, not the subjunctive.</p>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
